import React, {useState} from 'react';
import {User, UserData} from "types/User";
import {Form, Formik, FormikHelpers} from "formik";
import Label from "components/Form/Label";
import Button from "components/Button";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Selector from "components/Form/Selector";
import {toTitleCase} from "services/string";
import IconName from "types/IconName";
import {useAuth} from "../../../context/AuthContext";
import Uploader from "../../../components/Form/Uploader";
import {PhotographIcon} from "@heroicons/react/outline";
import {RoleName} from "../../../types/Role";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import BooleanIndicator from "../../../components/BooleanIndicator";
import {AppPermissionsList} from "../../../components/AppPermissionsList";
import {PasswordField} from "../../../components/Form/PasswordField";

// Types ----

type UserFormProps = {
  user?: User | null
  onSubmit: (values: UserData, helpers: FormikHelpers<UserData>) => any
}

// Context ----

function UserForm(props: UserFormProps) {
  const auth = useAuth();

  const [newPassword, setNewPassword] = useState<boolean>(!props.user?.id)

  const {data: roles, isLoading: rolesLoading} = useQuery(
    ['roles.all'],
    ngage.roles.all,
    {enabled: auth.can('roles:read')}
  );

  const selectableRoles = (roles ?? [])
    .filter(role => {
      const excludedRoles: RoleName[] = ['audience', 'volunteer'];
      return !excludedRoles.includes(role.name);
    })
    .sort((a, b) => a.id - b.id)
    .map(role => ({
      id: role.id,
      name: toTitleCase(role.name),
      description: role.description,
      icon: IconName.USER_CIRCLE
    }))

  const validationSchema = Yup.object().shape({
    role_ids: Yup.array().of(Yup.number()).min(1, "This field is required").required("This field is required"),
    name: Yup.string()
      .required("This field is required")
      .min(2, 'Minimum of 2 characters')
      .max(74, 'Maximum of 74 characters'),
    email: Yup.string()
      .email('Must be a valid email address')
      .required('This field is required')
      .max(255, 'Maximum of 255 characters'),
    password: newPassword ? Yup.string()
      .required("This field is required")
      .max(200, 'Maximum of 200 characters')
      .matches(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{10,}$/, 'Password must be at least 10 characters long and contain at least one uppercase letter, one lowercase letter, and one number.')
      : Yup.string().notRequired(),
  })

  const initialValues: UserData = {
    role_ids: props.user ? props.user.roles.map(role => role.id) : [],
    name: props.user?.name || '',
    email: props.user?.email || '',
    password: '',
    is_claimed: true,
    profile_picture_id: props.user?.profile_picture?.id || null,
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />
            
            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Account Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Enter the basic account details for the user.
                      </p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-2">
                        <Label htmlFor="name">Role *</Label>
                        <div className="mt-1">
                          <Selector
                            isLoading={rolesLoading}
                            items={selectableRoles}
                            selectedId={initialValues.role_ids?.[0]}
                            onChange={role => formik.setFieldValue('role_ids', [role.id])}
                          />

                          {(!!formik.errors.role_ids && formik.touched.role_ids) &&
                            <FieldHelp isError>
                              {formik.errors.role_ids}
                            </FieldHelp>
                          }
                        </div>
                      </div>

                      <div className="sm:col-span-3 sm:col-start-1">
                        <Label htmlFor="name">Name *</Label>
                        <div className="mt-1">
                          <Field
                            type="text"
                            name="name"
                            id="name"
                            autoComplete="name"
                            maxLength="74"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="sm:col-span-3 sm:col-start-1">
                        <Label htmlFor="email">Email *</Label>
                        <div className="mt-1">
                          <Field
                            type="email"
                            name="email"
                            id="email"
                            autoComplete="email"
                            maxLength="255"
                          />
                        </div>
                      </div>

                      {/* TODO: This is a temporary solution to prevent Audience Managers changing passwords */}
                      {(!auth.roles.includes('audience manager') && !newPassword) &&
                        <div className="sm:col-span-2 sm:col-start-1">
                          <Button
                            color={Button.colors.SECONDARY}
                            onClick={() => setNewPassword(true)}>
                            Change Password
                          </Button>
                        </div>
                      }

                      {newPassword &&
                        <div className="sm:col-span-3 sm:col-start-1">
                          <PasswordField
                            id="password"
                            label="Password *"
                            name="password"
                            maxLength={200}
                            autoFocus={!!props.user?.id}
                          />
                        </div>
                      }
                    </div>

                    {/* Media */}
                    <div className="pt-8">
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          Media
                        </h3>
                      </div>
                      <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        {/* Files */}
                        <div className="sm:col-span-6">
                          <Uploader
                            id={'profile_picture_id'}
                            name={'profile_picture_id'}
                            icon={PhotographIcon}
                            files={props.user?.profile_picture}
                            type={Uploader.types.IMAGE}
                            maxFiles={1}
                            onChange={files => formik.setFieldValue('profile_picture_id', files[0]?.id ?? null)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Two-factor Authentication */}
                    <div className="pt-8">
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          Two-factor Authentication (2FA)
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          This adds an extra layer of security to the users account by requiring more than just a password to sign in.
                        </p>
                      </div>

                      <div className="flex items-center gap-2 mt-4">
                        <p className={`m-0 p0 text-sm font-medium text-green-600 ${!!props.user?.two_factor_confirmed ? 'text-green-600' : 'text-red-600'}`}>
                          {!!props.user?.two_factor_confirmed ? 'Enabled' : 'Disabled'}
                        </p>
                        <BooleanIndicator value={!!props.user?.two_factor_confirmed}/>
                      </div>
                    </div>

                    {/* App Permissions */}
                    <div className="pt-8">
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          App Permissions
                        </h3>
                      </div>

                      <div className="flex items-center gap-2 mt-4">
                        <AppPermissionsList permissions={props.user?.app_permissions} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <FormControls formik={formik}/>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

export default UserForm;